import { useRef, useEffect, useMemo, useState } from "react";
import { useTranslation } from "@coworker/locales";
import { useParams } from "react-router";
import FullScreenPopup from "@coworker/apprestructured/src/layout/components/FullScreenPopup/FullScreenPopup";
import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import { FixaModal } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModal";
import { FixaPrompt } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaPrompt";
import { FixaModalFooter } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalFooter";
import { FixaModalHeader } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalHeader";
import ChevronRightIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/ChevronRightIconPath";
import FixaSSRIcon from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/FixaSSRIcon";
import {
  fullMediaName,
  getMainAndAlternativeProducts,
  getMediaTypeText,
  updateUnknownArticles,
} from "../tools";
import { logEvent } from "@coworker/app/src/helpers/tracker";
import { useMediaById } from "../hooks/useMediaById";
import { MediaViewSkeleton } from "../Skeletons/MediaViewSkeleton";
import { MediaImagesCarousel } from "../Molecules/MediaImagesCarousel";
import { MediaTabs } from "../Organisms/MediaTabs";
import { useCheckImageUsage } from "../hooks/useCheckImageUsage";
import { ModalTypes } from "../types/views";
import { FullWidthButton } from "../../SkapaButton";
import { PleaseFillIn } from "../../Reusable/PleaseFillIn";
import { ArticleIdNbrArticles, Articles } from "../types/article";
import { MediaArticlesModals } from "../Organisms/Modals/MediaArticlesModals";
import { Group } from "../types/groups";
import { useInputPopup } from "../../InputPopup";
import { useQueryClient } from "@tanstack/react-query";
import { useGroupsByMediaId } from "../hooks/useGroupsByMediaId";
import {
  useArticlesByMediaId,
  useSyncCombinationArticles,
} from "../hooks/useArticlesByMediaId";
import { QueryKeys } from "../hooks/queryKeys";
import { TabIds, TabQueryKey } from "../Organisms/MediaTabs.helper";
import { useSearchParams } from "react-router";
import { generateLivligProductsLink } from "@coworker/reusable/helpers/livligIntegration";
import { LivligLinkBox } from "../../LivligLinkBox";
import { sortArticlesByItemName } from "../../../helpers/sorters";
import featureNames from "@coworker/enums/featureNames";

import Linkbar from "@coworker/apprestructured/src/shared/components/Linkbar/Linkbar";
import { useCombinedArticles } from "../hooks/useCombinedArticles";
import { Media } from "../types/media";
import useFixaNavigate from "@coworker/apprestructured/src/shared/wrappers/FixaNavigate/useFixaNavigate";
import { MessageAndButton } from "../Molecules/MessageAndButton";
import { MediaHeader } from "../Molecules/MediaHeader";
import { MediaTypes } from "../constants";
import { Home } from "../Organisms/Home";
import SimpleArticleCardSkeleton from "@coworker/apprestructured/src/shared/simple/SimpleSkeleton/SimpleArticleCardSkeleton";
import {
  extractCombinationArticles,
  selectedArticleIdsToArticles,
} from "../Helpers/articleHelpers";
import { useToastNotification } from "@coworker/components/src/components/ToastNotification";
import { processArticles } from "../Helpers/articleHelpers";
import {
  useStoreId,
  useCountryId,
} from "@coworker/app/src/core/auth/useLoggedInUser";
import { useCombinationArticle } from "../hooks/useCombinationArticle";
import {
  mapCombinationArticlesByNo,
  mapGroupedCombinationArticles,
  diffCombinations,
} from "../utils/localCombinationDiff";
import { ensureArray } from "@coworker/utils/array.helpers";
import { UNKNOWN_ITEM_NAME } from "@coworker/apprestructured/src/shared/constants/itemType";
import styled from "styled-components";
import {
  ImpactedArticleCard,
  ImpactedArticleSummary,
} from "../Molecules/ImpactedArticleCard";

const UpdateImagesBanner = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 16px;
  background-color: #0058a3;
  border: none;
  cursor: pointer;
  text-align: left;
`;

const UpdateImagesBannerText = styled.span`
  font-family: "IKEA FONT VARIABLE", "Noto IKEA", sans-serif;
  font-weight: 700;
  font-size: 1rem;
  color: #ffffff;
  line-height: 1.4;
`;

const ImpactModalHeadline = styled.div`
  font-family: "IKEA FONT VARIABLE", "Noto IKEA", sans-serif;
  font-weight: 700;
  font-size: 1.5rem;
  line-height: 125%;
  text-align: center;
  margin-bottom: 16px;
`;

const ImpactModalDescription = styled.div`
  font-family: "IKEA FONT VARIABLE", "Noto IKEA", sans-serif;
  font-weight: 700;
  font-size: 1rem;
  line-height: 150%;
  text-align: center;
  margin-bottom: 16px;
`;

const ImpactModalPillRow = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 16px;
`;

const OutdatedImagesPill = styled.div`
  display: inline-flex;
  align-items: center;
  background-color: #c00000;
  color: #ffffff;
  font-family: "IKEA FONT VARIABLE", "Noto IKEA", sans-serif;
  font-weight: 700;
  font-size: 0.875rem;
  padding: 6px 16px;
  border-radius: 100px;
`;

const ImpactModalImageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
  margin-bottom: 24px;
`;

const ImpactModalGridImage = styled.img`
  width: 100%;
  aspect-ratio: 1;
  object-fit: cover;
  border-radius: 4px;
`;

const ImpactModalSectionTitle = styled.div`
  font-family: "IKEA FONT VARIABLE", "Noto IKEA", sans-serif;
  font-weight: 700;
  font-size: 1.25rem;
  line-height: 125%;
  text-align: left;
  margin-bottom: 12px;
`;

export const TabbedMediaView = () => {
  const [isAddingArticles, setIsAddingArticles] = useState(false);
  const userCountryId = useCountryId();
  const { mediaId } = useParams();
  const countryId = useCountryId();
  const storeId = useStoreId();
  const { t } = useTranslation();
  const [queryParams] = useSearchParams();
  const tabId = queryParams.get(TabQueryKey);
  const { getInput } = useInputPopup();
  const queryClient = useQueryClient();
  const navigate = useFixaNavigate();
  const { showToast } = useToastNotification();
  const { data: mediaResponse } = useMediaById(mediaId || "");
  const { data: groups } = useGroupsByMediaId(mediaId ?? "");

  const { data: articles, isError } = useArticlesByMediaId(
    mediaResponse?.media.id || "",
    "ACTIVE"
  );

  const hasSentGAEvent = useRef(false);

  useEffect(() => {
    if (
      !isError &&
      articles &&
      articles.length > 0 &&
      !hasSentGAEvent.current
    ) {
      const unknownArticlesCount = articles.filter(
        (article) => article.itemName.toLowerCase() === UNKNOWN_ITEM_NAME
      ).length;

      if (unknownArticlesCount > 0) {
        updateUnknownArticles(articles);

        logEvent("cnt_unknown_articles_fetched_per_media", {
          count: unknownArticlesCount,
          media_id: mediaResponse?.media.id,
        });
        hasSentGAEvent.current = true;
      }
    }
  }, [articles, isError]);

  const AllArticlesInMedia =
    useCombinedArticles(
      countryId,
      mediaResponse?.media.storeId ?? "",
      mediaId ?? "",
      articles
    ) ?? [];

  // Process articles to separate active and combination articles
  const {
    activeArticles,
    articlesInCombination,
    groupedCombinationArticles,
    combinationArticlesCollection,
  } = useMemo(() => processArticles(AllArticlesInMedia), [AllArticlesInMedia]);

  const { data: latestCombinationArticlesData } = useCombinationArticle(
    storeId,
    userCountryId,
    extractCombinationArticles(AllArticlesInMedia)
  );

  const { mutate: syncArticles } = useSyncCombinationArticles(
    combinationArticlesCollection
  );

  useEffect(() => {
    if (!latestCombinationArticlesData || !groupedCombinationArticles) return;

    const SYNC_INTERVAL_HOURS = 3;
    const SYNC_KEY = `combinationSync_${mediaId}`;
    const lastSync = localStorage.getItem(SYNC_KEY);
    const now = Date.now();

    if (
      lastSync &&
      now - Number(lastSync) < SYNC_INTERVAL_HOURS * 60 * 60 * 1000
    ) {
      return; // Skip sync if interval hasn't passed
    }

    // Normalise latest and grouped combinations by article number, then diff
    const latestCombinationsByNo = mapCombinationArticlesByNo(
      ensureArray(latestCombinationArticlesData)
    );
    const groupedCombinationsByNo = mapGroupedCombinationArticles(
      groupedCombinationArticles
    );

    const { toAdd, toUpdate, toDelete } = diffCombinations(
      ensureArray(latestCombinationArticlesData),
      latestCombinationsByNo,
      groupedCombinationsByNo,
      combinationArticlesCollection,
      articlesInCombination
    );

    // Only sync if there are diffs
    if (toAdd.length || toUpdate.length || toDelete.length) {
      syncArticles({
        toAdd,
        toUpdate,
        toDelete,
      });
      localStorage.setItem(SYNC_KEY, String(now)); // Update last sync time
    }
  }, [
    latestCombinationArticlesData,
    groupedCombinationArticles,
    articlesInCombination,
    combinationArticlesCollection,
    mediaId,
    syncArticles,
  ]);

  const [modalVisibleByType, setModalVisibleByType] = useState<ModalTypes>(
    ModalTypes.DEFAULT_VIEW
  );
  const [currentTab, setCurrentTab] = useState<string>(
    tabId === TabIds.GROUP ? TabIds.GROUP : TabIds.LIST
  );
  const [selectedArticles, setSelectedArticles] = useState<Articles>([]);
  const [isSelectArticlesMode, setIsSelectArticlesMode] = useState(false);
  const [warningText, setWarningText] = useState("");
  const [isImpactModalOpen, setIsImpactModalOpen] = useState(false);
  const [openAddImagesFromImpact, setOpenAddImagesFromImpact] = useState(false);
  const [currentArticleIndex, setCurrentArticleIndex] = useState(0);

  // Check for image usage in articles
  const { hasImageUsage, impactedImages } = useCheckImageUsage(mediaId);

  const impactedArticles = useMemo<ImpactedArticleSummary[]>(() => {
    const itemNos = new Set<string>();
    for (const image of impactedImages) {
      const links = Array.isArray(image.deletedItemLinks)
        ? image.deletedItemLinks
        : [];
      for (const link of links) {
        if (link?.itemNo) itemNos.add(link.itemNo);
      }
    }

    return Array.from(itemNos).map((itemNo) => {
      const matchedImpactedImage = impactedImages.find((image) =>
        (image.deletedItemLinks ?? []).some((link) => link.itemNo === itemNo)
      );
      return {
        productArticleId: itemNo,
        fallbackImageUrl: matchedImpactedImage?.url,
      };
    });
  }, [impactedImages]);

  let fetchSelectedArticleIdsFunction: () => ArticleIdNbrArticles[] = () => [];

  const mainAndAlternativeProduct = getMainAndAlternativeProducts(
    mediaResponse?.media as Media
  );
  const mediaHeader = fullMediaName(
    mediaResponse?.media.name,
    mainAndAlternativeProduct?.mainProductName1,
    mainAndAlternativeProduct?.mainProductName2
  );

  const linkbarItems = useMemo(() => {
    const items = [
      {
        label: t("mediaMaintenanceString"),
        to: "/mediamaintenance",
      },
    ];
    if (
      mediaResponse?.media.mediaType === MediaTypes.ROOM &&
      !!mediaResponse?.parent
    ) {
      items.push({
        label: mediaResponse?.parent.name,
        to: `/mediamaintenance/tabs/${mediaResponse?.parent.id}?tab=article-list-tab`,
      });
    }
    return items;
  }, [t, mediaResponse]);

  const linkbar = <Linkbar items={linkbarItems} currentLabel={mediaHeader} />;

  const articlesWasAdded = useRef(isAddingArticles);
  useEffect(() => {
    if (articlesWasAdded.current && !isAddingArticles) {
      showToast(t("articlesAddedSuccessfullyString"), undefined, undefined, 2);
    }
    articlesWasAdded.current = isAddingArticles;
  }, [isAddingArticles]);

  if (isError) {
    return (
      <FullScreenPopup noPadding linkbar={linkbar}>
        <MessageAndButton
          keyForMessageText={"couldNotLoadArticlesString"}
          keyForButtonText={"tryAgainString"}
          buttonAction={async () => {
            window.location.reload();
          }}
        />
      </FullScreenPopup>
    );
  }

  if (!mediaResponse) {
    return (
      <FullScreenPopup noPadding linkbar={linkbar}>
        <MediaHeader
          title={mediaHeader}
          type={MediaTypes.NONE}
          onEditClick={() => {}}
        />
        <MediaViewSkeleton />
      </FullScreenPopup>
    );
  }

  if (isAddingArticles)
    return (
      <FullScreenPopup noPadding linkbar={linkbar}>
        <MediaHeader
          title={mediaHeader}
          type={mediaResponse?.media.mediaType as MediaTypes}
          onEditClick={() => {}}
        />
        {[...Array(3)].map((_, index) => (
          <SimpleArticleCardSkeleton key={index} />
        ))}
      </FullScreenPopup>
    );

  return (
    <FullScreenPopup
      isActionBarHiddenOnScroll={false}
      actionBarContent={
        <>
          {mediaResponse?.media.mediaType !== MediaTypes.HOME ? (
            <>
              {currentTab === TabIds.GROUP && (
                <FullWidthButton
                  text={t("createGroupString")}
                  onClick={() => setModalVisibleByType(ModalTypes.CREATE_GROUP)}
                  type={"primary"}
                />
              )}
              {(!currentTab || currentTab === TabIds.LIST) &&
                !isSelectArticlesMode && (
                  <FullWidthButton
                    text={t("addArticlesString")}
                    onClick={async () => {
                      await getInput("addMultipleArticles", {
                        mediaId,
                        groupId: "",
                        setIsAddingArticles,
                      });
                      await queryClient.invalidateQueries({
                        queryKey: [QueryKeys.ArticlesByMedia],
                      });
                    }}
                    type={"primary"}
                    data-testid="tabbedMediaView:addArticlesButton"
                  />
                )}
              {(!currentTab || currentTab === TabIds.LIST) &&
                isSelectArticlesMode && (
                  <>
                    <PleaseFillIn
                      show={!!warningText}
                      alternateWarningTranslation={warningText}
                      centerText={true}
                    />
                    <FullWidthButton
                      text={t("continue2String")}
                      onClick={async () => {
                        const selectedArticleIds =
                          fetchSelectedArticleIdsFunction();
                        if (selectedArticleIds.length > 0) {
                          setModalVisibleByType(
                            ModalTypes.SELECTED_ARTICLES_ACTIONS
                          );
                          setSelectedArticles(
                            selectedArticleIdsToArticles(
                              activeArticles,
                              selectedArticleIds,
                              articlesInCombination
                            )
                          );
                        } else {
                          setWarningText(t("selectArticlesToContinueString"));
                        }
                      }}
                      type={"primary"}
                    />
                  </>
                )}
            </>
          ) : (
            <FullWidthButton
              text={t("addSomethingString", {
                name: getMediaTypeText(MediaTypes.ROOM),
              })}
              onClick={() => setModalVisibleByType(ModalTypes.ADD_ROOM)}
              type={"primary"}
            />
          )}
        </>
      }
      noPadding
      fullHeightContent
      linkbar={linkbar}
    >
      <>
        <MediaHeader
          title={mediaHeader}
          type={mediaResponse?.media.mediaType as MediaTypes}
          onEditClick={() => {
            navigate(`/mediamaintenance/update/${mediaId}`);
          }}
        />
        <MediaImagesCarousel
          media={mediaResponse.media}
          externalAddImagesOpen={openAddImagesFromImpact}
          onExternalAddImagesClose={() => setOpenAddImagesFromImpact(false)}
        />
        {hasImageUsage &&
          mediaResponse?.media.mediaType !== MediaTypes.HOME && (
            <UpdateImagesBanner
              onClick={() => {
                setCurrentArticleIndex(0);
                setIsImpactModalOpen(true);
              }}
            >
              <UpdateImagesBannerText>
                {t("updateImagesAfterRemovingArticlesString")}
              </UpdateImagesBannerText>
              <FixaSSRIcon paths={ChevronRightIconPath} color="#ffffff" />
            </UpdateImagesBanner>
          )}
        {mediaResponse?.media.mediaType === MediaTypes.HOME && (
          <Home rooms={mediaResponse.children} />
        )}
        {mediaResponse?.media.mediaType !== MediaTypes.HOME && (
          <>
            {activeArticles && activeArticles.length > 0 && (
              <LivligLinkBox
                storeId={mediaResponse.media.storeId}
                shortIds={activeArticles
                  .sort(sortArticlesByItemName)
                  .map((article) => article.productArticleId)
                  .join(",")}
                generateLivligLinkFunc={generateLivligProductsLink}
                feature={featureNames.MEDIA}
                titleKey={"livligViewInString"}
                descriptionKey={"livligSalesPerformanceOfArticlesString"}
                buttonTextKey={"livligViewInString"}
              />
            )}
            <MediaTabs
              media={mediaResponse.media}
              activeArticles={activeArticles}
              combinationArticles={groupedCombinationArticles}
              isSelectArticlesMode={isSelectArticlesMode}
              getSelectedArticleIds={(
                fetchFunction: () => ArticleIdNbrArticles[]
              ) => {
                fetchSelectedArticleIdsFunction = fetchFunction;
              }}
              setSelectArticleMode={() => {
                setIsSelectArticlesMode(!isSelectArticlesMode);
                setWarningText("");
              }}
              allArticlesInMedia={activeArticles || []}
              onCurrentTabIdChange={(tab: string) => {
                setCurrentTab(tab);
                setIsSelectArticlesMode(false);
                const newUrl = `${window.location.origin}${window.location.pathname}?${TabQueryKey}=${tab}`;
                window.history.replaceState({}, "", newUrl);
              }}
              currentTabId={currentTab}
              groups={groups}
            />
          </>
        )}
      </>
      {modalVisibleByType !== ModalTypes.DEFAULT_VIEW && (
        <MediaArticlesModals
          selectedArticles={selectedArticles}
          allArticles={activeArticles || []}
          currentModalType={modalVisibleByType}
          onModalTypeChange={(modalType: ModalTypes) => {
            if (modalType === ModalTypes.DEFAULT_VIEW) {
              setIsSelectArticlesMode(false);
              setSelectedArticles([]);
            }
            setWarningText("");
            setModalVisibleByType(modalType);
          }}
          groups={groups || []}
          storeId={mediaResponse.media.storeId}
          mediaId={mediaResponse.media.id ?? ""}
          currentGroup={{} as Group}
          media={mediaResponse}
        />
      )}
      {isImpactModalOpen && (
        <FixaModal
          visible={true}
          handleCloseBtn={() => setIsImpactModalOpen(false)}
        >
          <FixaPrompt
            title={""}
            titleId="impacted-images-modal"
            header={<FixaModalHeader ariaCloseTxt="Close prompt." />}
            footer={
              <FixaModalFooter>
                <FixaButton
                  text={t("cancelString")}
                  type="secondary"
                  onClick={() => setIsImpactModalOpen(false)}
                  fluid
                />
                <FixaButton
                  text={t("uploadNewImagesString")}
                  type="primary"
                  onClick={() => {
                    setIsImpactModalOpen(false);
                    setOpenAddImagesFromImpact(true);
                  }}
                  fluid
                />
              </FixaModalFooter>
            }
          >
            <>
              <ImpactModalHeadline>
                {t("impactedImagesHeadlineString", {
                  count: impactedImages.length,
                })}
              </ImpactModalHeadline>
              <ImpactModalDescription>
                {t("impactedImagesDescriptionString")}
              </ImpactModalDescription>
              <ImpactModalPillRow>
                <OutdatedImagesPill>
                  {t("outdatedImagesString")}
                </OutdatedImagesPill>
              </ImpactModalPillRow>
              <ImpactModalImageGrid>
                {impactedImages.map((image, index) => (
                  <ImpactModalGridImage
                    key={
                      image.id ?? image.fileName ?? `impacted-image-${index}`
                    }
                    src={image.url || image.fileName || ""}
                    alt={image.fileName || `Impacted image ${index + 1}`}
                  />
                ))}
              </ImpactModalImageGrid>
              {impactedArticles.length > 0 && (
                <>
                  <ImpactModalSectionTitle>
                    {t("deletedArticlesString")}
                  </ImpactModalSectionTitle>
                  <ImpactedArticleCard
                    article={impactedArticles[currentArticleIndex]!}
                    showChevron={impactedArticles.length > 1}
                    onChevronClick={() =>
                      setCurrentArticleIndex(
                        (prev) => (prev + 1) % impactedArticles.length
                      )
                    }
                  />
                </>
              )}
            </>
          </FixaPrompt>
        </FixaModal>
      )}
    </FullScreenPopup>
  );
};
